import { Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';
import { SubscriptionBirthFamilyCrumbs } from 'subscription/constants/breadcrumb';
import { transformerSubscriptionBirthData } from 'subscription/utils/transformer-anpr';
import { SubscriptionBirthFamilyFormVM } from 'subscription/view-models/subscription-registration-birth-family';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import validationSchemaBirthFamily from './validation-schema';
import PmiTitle from 'shell/components/shared/title';
import SubscriptionBirthFamily from 'subscription/components/birth-family';
import { DateTime } from 'luxon';
import { ActType } from 'shell/constants/act-type';
import { ActManagementForm } from 'shell/view-models/act-form';
import { transformerRequestSubscriptionBirthFamilyData } from 'subscription/utils/transformer-request-birth-family';
import { emptyPaper } from 'shell/components/shared/act/constants';
import SubscriptionButtons from 'subscription/components/shared/buttons';

const emptyActManagement: ActManagementForm = {
  actType: ActType.AttoCartaceo,
  paper: emptyPaper,
};

interface SubscriptionBirthFamiliyPageProps {
  isWithoutBreadcrumbs?: boolean;
  disabledFromAct?: boolean;
  goBackUrl?: string;
  initialValues?: SubscriptionBirthFamilyFormVM;
  onConfirm?: () => void;
  onHandleExit?: () => void;
  isFromCivilStatus?: boolean;
}

const SubscriptionBirthFamiliyPageComponent = ({
  isWithoutBreadcrumbs,
  goBackUrl = '/iscrizioni',
  initialValues = {},
  onConfirm,
  onHandleExit,
  disabledFromAct,
  isFromCivilStatus,
}: SubscriptionBirthFamiliyPageProps): React.ReactElement => {
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, SubscriptionBirthFamilyFormVM>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: (response) => {
        const transformedData = transformerSubscriptionBirthData(response);
        return {
          ...transformedData,
          editDataControl: {
            ...transformedData.editDataControl,
            dateValidity: DateTime.now(),
            requested: '',
            assume: '',
            subscribeTo: '',
            flag: '',
          },
          citizenship: {
            ...transformedData.citizenship,
            validity: null,
            state: null,
          },
          birthdayDocument: emptyActManagement,
        };
      },
    }
  );

  const [responseRegisterBirth, registerBirth] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/iscrizione-nascita`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedSavedData = responseRegisterBirth.data as SaveDataAnpr;

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  // on change visibility
  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const handleSubmit = useCallback(
    (values: SubscriptionBirthFamilyFormVM) => {
      const data = transformerRequestSubscriptionBirthFamilyData(values);

      return (
        registerBirth({
          data,
        }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;

        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }

        if (onConfirm) {
          onConfirm();
          return;
        }

        toast.success('Operazione eseguita con successo');
        navigate(goBackUrl);
      });
    },
    [navigate, registerBirth, setVisibility, onConfirm, goBackUrl]
  );

  //Errors
  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedSavedData?.errors?.length && visibility.error === undefined) {
      errorToVisualize.title =
        castedSavedData?.errors?.length > 1
          ? `Ci sono ${castedSavedData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedSavedData]);

  const handleExit = useCallback(() => {
    if (onHandleExit) {
      onHandleExit();
      return;
    }

    navigate(goBackUrl);
  }, [navigate]);

  return (
    <Fragment>
      {isWithoutBreadcrumbs ? undefined : <Breadcrumbs items={SubscriptionBirthFamilyCrumbs} />}
      <div>
        <PmiTitle title="Iscrizione Nascita Famiglia" />
      </div>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={`Ci sono ${castedSavedData?.warnings?.length || ''} warning`}
        messages={castedSavedData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (castedSavedData?.warnings || [])?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      {anpr.data && !anpr.pristine && !anpr.isLoading && !anpr.hasErrors ? (
        <Formik
          initialValues={{
            ...((anpr.data as SubscriptionBirthFamilyFormVM) || {}),
            ...(initialValues || {}),
            familyCoexistence: {
              ...((anpr.data as SubscriptionBirthFamilyFormVM) || {}).familyCoexistence,
              ...(initialValues?.familyCoexistence || {}),
            },
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchemaBirthFamily}
        >
          <Form>
            <Grid container spacing={2}>
              <SubscriptionButtons handleExit={handleExit} />
              <Grid item xs={12} md={10}>
                <SubscriptionBirthFamily
                  disabledFromAct={disabledFromAct}
                  isFromCivilStatus={isFromCivilStatus}
                  isSubscription
                />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      ) : undefined}
    </Fragment>
  );
};

SubscriptionBirthFamiliyPageComponent.displayName =
  'Subscription Birth cohabitation page component';

const SubscriptionBirthFamiliyPage = memo(SubscriptionBirthFamiliyPageComponent);

export default SubscriptionBirthFamiliyPage;
